<template>
  <Modal
    :visible="showRef"
    :max-width="65"
    :style-class="classModal"
    :style-class-contain="styleClassContain"
    :is-hide-overlay="isHideOverlay"
    :class-name="'edit-modal-action !tw-translate-y-[0]'"
    :should-scroll-to-bottom="shouldScrollToBottom"
    @close="onCloseModal"
  >
    <ModalHeader
      :class="`modal-header`"
      :title="
        getComponentUiText(
          'app-assess-recommendations-edit-action-modal-header-1',
        )
      "
      @close="handleCloseModal"
    />

    <!-- Modal container -->
    <div class="tw-overflow-auto hiddenBorderFirstChild modalContainer">
      <template v-for="item in arrayData" :key="item.key">
        <div
          class="tw-px-[20px] tw-py-[40px] sm:tw-p-[40px] tw-border-0 tw-border-t tw-border-solid tw-border-[#EFF0F1] tw-flex tw-flex-col tw-gap-[25px]"
        >
          <div
            class="tw-flex tw-flex-col tw-justify-between tw-w-full tw-gap-2.5"
          >
            <div class="tw-flex tw-items-center tw-gap-2.5 tw-h-[21px]">
              <span
                class="tw-text-[#211F58] tw-text-[16px] tw-font-[600] tw-not-italic tw-min-h-[21px]"
              >
                {{ item.title }}
              </span>
            </div>
            <span
              v-if="item.description"
              class="tw-text-[#878787] tw-text-[13px] tw-font-[400] tw-leading-normal tw-min-h-[20px]"
              >{{ item.description }}</span
            >
          </div>
          <component
            :is="item.component"
            v-if="item?.component"
            ref="ActionDetailsRef"
            :action="actionData"
            :in-modal-edit="true"
            @update:data="
              (data) => {
                isDirty = true;
                actionData = Object.assign(actionData, data);
              }
            "
          ></component>
        </div>
      </template>
      <!-- button save -->
      <div class="tw-flex tw-justify-end tw-items-center tw-pb-10">
        <b-button
          style="height: 39px"
          variant="primary"
          :class="`tw-shadow-none tw-bg-[#387DFF] tw-py-[0px] tw-px-[18px] tw-text-[15px] tw-font-medium tw-rounded-[27px] tw-mr-10`"
          type="submit"
          @click="saveDataStepsToSolve"
        >
          <LottieAnimation
            v-if="isLoadingAPI"
            :animation-data="ProgressAnimation"
            :auto-play="true"
            :loop="true"
            :class="'tw-h-[39px] tw-w-[37px]'"
          />
          <span>
            {{
              getComponentUiText(
                "app-component-assessment-modal-assessment-save-button-1",
              )
            }}
          </span>
        </b-button>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import ModalHeader from "@/components/common/modalHeader.vue";
import { editSteps, getComponentUiText, removeSteps } from "@/utils/utilities";
import { ref, watch, PropType, onUnmounted, computed, nextTick } from "vue";
import Modal from "@/components/common/modal.vue";
import {
  showErrorAPI,
  showSuccessToast,
  showToastMessage,
} from "@/services/apiRoutes";
import ActionDetail from "./editActionModalElement/actionDetail.vue";
import Questions from "./editActionModalElement/questions.vue";
import StepToSolve from "./editActionModalElement/stepToSolve.vue";
import cloneDeep from "lodash/cloneDeep";
import { addStep } from "@/services/recommend";
import store from "@/store";
import eventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { Checklist } from "@/services/apiRoutes/routes";
import { ToastVariant } from "@/interfaces/common/IToastMessage";
import { LottieAnimation } from "lottie-web-vue";
import ProgressAnimation from "../../assets/animation/progress_dark.json";

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  isHideOverlay: {
    type: Boolean,
    default: true,
  },
  action: {
    type: Object as PropType<any>,
    required: true,
  },
  isEditActionTable: {
    type: Boolean,
    required: false,
    default: false,
  },
  shouldScrollToBottom: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const showRef = ref(props.show);
const isDirty = ref(false);
const actionData = ref(cloneDeep(props.action));
const checklistId = computed(() => {
  return store.state.Recommend.checklistReportId;
});
const ActionDetailsRef: any = ref(null);
const isLoadingAPI = ref(false);

watch(
  () => props.show,
  (value) => {
    showRef.value = value;
  },
);

watch(
  () => props.action,
  (value) => {
    actionData.value = cloneDeep(props.action);
  },
);

const emit = defineEmits(["update:show", "update:data"]);

const onCloseModal = () => {
  if (isDirty.value) {
    return;
  }
  handleCloseModal();
};
const handleCloseModal = () => {
  showRef.value = false;
  emit("update:show", showRef.value);
};

const isChangeEditAction = computed(() => {
  return store.getters["Recommend/isChangeEditAction"];
});

const updateSuggestedAction = () => {
  if (isChangeEditAction.value) {
    store.dispatch(
      "Recommend/updateSuggestedAction",
      actionData.value.checklistGroupTemplateId,
    );
  }
};

const classModal = {
  background: "rgba(35, 31, 32, 0.8999999761581421)",
};

const styleClassContain = {
  "border-radius": "4px",
  "box-shadow": "0px 0px 4px 0px rgba(0, 0, 0, 0.05)",
};

onUnmounted(() => {
  store.commit("Recommend/SET_DATA_ACTION", {});
  store.commit("Recommend/SET_IS_CHANGE_EDIT_ACTION");
  store.commit("Recommend/SET_LIST_STEP_ACTIONS_PLAN", []);
  store.commit("Recommend/SET_LIST_STEP_REMOVED", null);
});

const arrayData = [
  {
    key: "ActionDetails",
    title: getComponentUiText(
      "app-assess-recommendations-edit-action-modal-action-detail-1",
    ),
    description: "",
    component: ActionDetail,
  },
  // {
  //   key: "Score overides",
  //   title: getComponentUiText(
  //     "app-assess-recommendations-edit-action-modal-score-1",
  //   ),
  //   description: getComponentUiText(
  //     "app-assess-recommendations-edit-action-modal-action-score-sub-title-1",
  //   ),
  //   component: ScoreOverides,
  // },
  {
    key: "Questions",
    title: getComponentUiText(
      "app-assess-recommendations-edit-action-modal-question-1",
    ),
    description: getComponentUiText(
      "app-assess-recommendations-edit-action-modal-action-question-sub-title-1",
    ),
    component: Questions,
  },
  {
    key: "StepsToSolve",
    title: getComponentUiText(
      "app-assess-recommendations-action-table-label-tab-action-plan-1",
    ),
    description: props.isEditActionTable
      ? getComponentUiText(
          "app-assess-recommendations-edit-action-modal-action-plan-sub-title-1",
        )
      : getComponentUiText(
          "app-assess-recommendations-edit-action-modal-action-step-sub-title-edit-1",
        ),
    component: StepToSolve,
    props: {
      isEditActionTable: props.isEditActionTable,
    },
  },
  // {
  //   key: "Service Providers",
  //   title: getComponentUiText(
  //     "app-assess-recommendations-edit-action-modal-service-1",
  //   ),
  //   description: getComponentUiText(
  //     "app-assess-recommendations-edit-action-modal-action-service-sub-title-1",
  //   ),
  //   component: ServiceProviders,
  // },
];

const listSteps = computed(() => {
  return store.state.Recommend.listStepActionsPlan;
});

const checkAllValidations = async () => {
  const isValidListSteps = listSteps.value.every(
    (item) => item.isValid === true,
  );
  return {
    isValid: isValidListSteps,
    listSteps: listSteps.value.map((item) => {
      const { isValid, ...rest } = item;
      return rest;
    }),
    listSrepRemove: store.state.Recommend.listStepRemoved,
    checkListGroupId: actionData.value.checklistGroupId,
  };
};

const saveDataStepsToSolve = async () => {
  store.commit("Recommend/SET_IS_CREATING_ACTION", true);
  setTimeout(() => {
    store.commit("Recommend/SET_IS_CREATING_ACTION", false);
  }, 100);

  const isValid = await ActionDetailsRef.value[0].saveChanges();
  await nextTick();
  let arr: any = [];
  const data = await checkAllValidations();
  if (data.isValid && data.checkListGroupId && !isValid) {
    isLoadingAPI.value = true;
    store.commit("Recommend/SET_LIST_STEP_ACTIONS_PLAN", []);
    store.commit("Recommend/SET_LIST_STEP_REMOVED", null);
    arr = [
      ...arr,
      props.isEditActionTable
        ? store.dispatch(
            "Recommend/updateAction",
            actionData.value.checklistGroupId,
          )
        : updateSuggestedAction(),
    ];

    if (data.listSrepRemove.length) {
      const listRemove = removeSteps(
        data.listSrepRemove,
        data.checkListGroupId,
        checklistId.value,
      );
      arr = [...arr, ...listRemove];
    }
    if (data.listSteps.length > 0) {
      const listEdit = editSteps(
        data.listSteps,
        data.checkListGroupId,
        checklistId.value,
      );
      arr = [...arr, ...listEdit];
      const listAdd = addSteps(data.listSteps, data.checkListGroupId);
      arr = [...arr, ...listAdd];
    }

    try {
      const res = await Promise.allSettled(arr);
      let error = 0;
      res.forEach((result, index) => {
        if (result.status === "fulfilled") {
          setTimeout(() => {
            handleCloseModal();
          }, 500);
          showSuccessToast(
            getComponentUiText("app-assess-recommend-step-message-1"),
          );
        } else if (result.status === "rejected") {
          error++;
        }
        if (error > 1) {
          showToastMessage(
            getComponentUiText("app-exception-handler-error-unknown-error"),
            ToastVariant.ERROR,
          );
        }
      });
      eventEmitter.emit(EmitterEvents.RELOAD_RECOMMENDATION);
    } catch (error) {
      showErrorAPI(error, Checklist.removeStep);
    } finally {
      setTimeout(() => {
        isLoadingAPI.value = false;
      }, 500);
    }
  }
};

const addSteps = (listSteps, checkListGroupId) => {
  try {
    const listFilterSrepAdd = listSteps.filter(
      (item) => !item?.checklistGroupActionId,
    );
    const promises = listFilterSrepAdd.map((item) => {
      const data = {
        checklistGroupActionHtml: item.checklistGroupActionHtml,
        checklistGroupActionName: item.checklistGroupActionName,
        checklistGroupActionOrder: item.checklistGroupActionOrder,
      };
      return addStep(checklistId.value, checkListGroupId, data);
    });
    return promises;
  } catch (error) {
    showErrorAPI(error, Checklist.addStep);
  }
};
onUnmounted(() => {
  store.commit("Recommend/SET_CHECKLIST_ASSESSMENT_QUESTIONS");
});
</script>

<style lang="scss" scoped>
.assessment-modal {
  .button-verify.btn.btn-primary[disabled] {
    // background-color: #dbdbdb;
    background-color: red;
    color: #b7b7b7;
    border-color: #dbdbdb;
  }

  .button-verify {
    line-height: 100%;
    background-color: #387dff;
    border-color: #387dff;
  }
}

@media (max-width: 768px) {
  .assessment-modal {
    .modal-assessment,
    .modal-header {
      padding-right: 10px;
      padding-left: 10px;
    }
  }
}
.hiddenBorderFirstChild > div:first-of-type {
  border: none !important;
}

.edit-modal-action {
  max-height: calc(100vh - 150px) !important;
  top: 103px !important;
}
</style>
